import * as React from "react";

import { cn } from "@/shared/lib/utils";
import { H2, Text } from "@/shared/ui/typography";

const Card = ({ ref, className, ...props }: React.ComponentProps<"div">) => (
  <div
    ref={ref}
    data-slot="card"
    className={cn(
      "rounded-xl border bg-card text-card-foreground shadow-sm glass-big",
      className,
    )}
    {...props}
  />
);
Card.displayName = "Card";

const CardHeader = ({
  ref,
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-10 pb-6", className)}
    {...props}
  />
);
CardHeader.displayName = "CardHeader";

const CardTitle = ({
  ref,
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <H2
    ref={ref}
    className={cn("font-semibold leading-none tracking-tight", className)}
    {...props}
  />
);
CardTitle.displayName = "CardTitle";

const CardDescription = ({
  ref,
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <Text ref={ref} className={cn(className)} {...props} />
);
CardDescription.displayName = "CardDescription";

const CardContent = ({
  ref,
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <div ref={ref} className={cn("p-10 pt-0", className)} {...props} />
);
CardContent.displayName = "CardContent";

const CardFooter = ({
  ref,
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <div
    ref={ref}
    className={cn("flex items-center p-10 pt-0", className)}
    {...props}
  />
);
CardFooter.displayName = "CardFooter";

const KanbanCard = ({
  ref,
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <div
    ref={ref}
    className={cn(
      // No outline at rest: the card is white on the board's white and reads by
      // its elevation alone. Hover lifts it and brings the hairline in — the
      // card is a link, and that is the whole of the feedback it needs. The
      // border stays in the box model at `transparent` so hover does not
      // reflow the card by a pixel.
      "relative flex flex-col rounded-lg border border-transparent bg-white text-card-foreground shadow-sm transition-all hover:border-neutral-200 hover:shadow-md",
      className,
    )}
    {...props}
  />
);
KanbanCard.displayName = "KanbanCard";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  KanbanCard,
};
